import { FormGroup } from '@angular/forms';

/**
 * Reorders a FormGroup's controls by moving the control at `fromIndex` to
 * `toIndex`, driven by PrimeNG's row-reorder drag/drop indices. Rebuilds the
 * FormGroup's `controls` insertion order (which `Object.keys` reflects) by
 * re-adding the same control instances in the new order. Generic over any
 * FormGroup whose direct children are the reorderable rows — shared by the
 * colour palette group table and the typography table.
 */
export function reorderFormGroupControls(formGroup: FormGroup, fromIndex: number, toIndex: number): void {
  // One change event for the whole move, so the theme saves the new order.
  if (moveFormGroupControl(formGroup, fromIndex, toIndex)) formGroup.updateValueAndValidity();
}

/** The move behind reorderFormGroupControls, without the change event. Returns whether anything moved. */
function moveFormGroupControl(formGroup: FormGroup, fromIndex: number, toIndex: number): boolean {
  const keys = Object.keys(formGroup.controls);
  if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0 || fromIndex >= keys.length || toIndex >= keys.length) {
    return false;
  }
  const [movedKey] = keys.splice(fromIndex, 1);
  keys.splice(toIndex, 0, movedKey);

  const controls = formGroup.controls;
  keys.forEach((key) => {
    const control = controls[key];
    formGroup.removeControl(key, { emitEvent: false });
    formGroup.addControl(key, control, { emitEvent: false });
  });
  return true;
}

/**
 * Renames a FormGroup's child control in place: plain `removeControl`+
 * `addControl` always re-appends at the end, which would visibly move the
 * row to the bottom of a table rendering that order, so the renamed control
 * is moved straight back to its original index afterwards. Shared by the
 * colour palette's group/colour renaming and the typography table's style
 * renaming.
 */
export function renameFormGroupControl(formGroup: FormGroup, oldName: string, newName: string): void {
  if (oldName === newName) return;
  const control = formGroup.get(oldName);
  if (!control) return;

  const originalIndex = Object.keys(formGroup.controls).indexOf(oldName);
  formGroup.removeControl(oldName, { emitEvent: false });
  formGroup.addControl(newName, control, { emitEvent: false });
  moveFormGroupControl(formGroup, Object.keys(formGroup.controls).length - 1, originalIndex);
  formGroup.updateValueAndValidity();
}
